import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, MultiSelectCheckbox, Select, Card, SelectRadioButton, Tabs, TabsItem } from '../../components/ui';
import { addSearchParams, downloadFile, getHref } from '../../helpers/download';
import styled from 'styled-components';
import Export from '../../icons/Export';
import { Role } from '../../store/conference/types';
import { AppState } from '../../store/state';
import { selectTrackRoles } from '../../store/selectors';
import { AppDispatch } from '../../store/store';
import { connect } from 'react-redux';
import AccessTokenManager from '../../components/AccessTokenManager/AccessTokenManager';

type FileFormat = 'json' | 'excel';

interface Props {
  roleById: { [key: string]: Role };
}

const DataAPIAccessPage: React.FC<Props> = ({ roleById }) => {
  const reviewerRoles = Object.values(roleById).filter((role) => role.type == 'reviewer');

  const initRoles = (): string => {
    return reviewerRoles.length == 1 ? reviewerRoles[0].id.toString() : '';
  };

  const { t } = useTranslation();
  const [value, setValue] = useState<string | undefined>(undefined);
  const [relationships, setRelationships] = useState<string[]>([]);
  const [role, setRole] = useState<string>(initRoles);
  const [format, setFormat] = useState<FileFormat | undefined>(undefined);
  const [section, setSection] = React.useState<'file' | 'api'>('file');

  const options = [
    {
      id: 'recruitment',
      label: 'Recruitment',
      relationships: [],
    },
    {
      id: 'people',
      label: 'People',
      relationships: [
        { id: 'roles', label: t('Roles') },
        { id: 'keywords', label: t('Keywords') },
      ],
    },
    {
      id: 'authors',
      label: 'Authors',
      relationships: [
        { id: 'submissions', label: t('Submissions') },
        { id: 'keywords', label: t('Keywords') },
      ],
    },
    {
      id: 'reviewers',
      label: 'Reviewers',
      relationships: [{ id: 'keywords', label: t('Keywords') }],
    },
    {
      id: 'submissions',
      label: 'Submissions',
      relationships: [
        { id: 'authors', label: t('Authors') },
        { id: 'keywords', label: t('Keywords') },
      ],
    },
    { id: 'bidding', label: t('Bidding'), relationships: [] },
    { id: 'assignments', label: t('Assignments'), relationships: [] },
    { id: 'reviews', label: t('Reviews'), relationships: [] },
  ];

  const selected = options.find((option) => option.id == value);

  const isRoleNeeded = ['reviewers', 'bidding', 'assignments', 'reviews'].includes(selected?.id ?? '');

  let content;
  switch (section) {
    case 'file':
      content = (
        <div className="flex flex-col justify-center items-center">
          <StyledMessageWrapper className="flex flex-col justify-center items-center">
            <StyledIcon>
              <Export />
            </StyledIcon>
            <h2 className="text-xl font-bold w-max mt-6">{t('What do you want to export?')}</h2>
            <p className="mt-4 mb-4">
              {t(
                'Easily download your data in the format you need. Export to Excel for spreadsheet analysis or to JSON for technical use and system integration.',
              )}
            </p>
          </StyledMessageWrapper>
          <div>
            <Select
              width="24rem"
              defaultItem={{ label: t('Choose option') }}
              options={options}
              value={value}
              onChange={(newValue) => {
                setValue(newValue);
                setRelationships([]);
                setRole(initRoles());
                setFormat(undefined);
              }}
            />
          </div>
          {selected && (
            <>
              {selected.relationships.length > 0 && (
                <StyledCard className="mt-6">
                  <p className="mb-4">
                    <b>{t('Do you need to include additional data?')}</b>
                  </p>
                  <MultiSelectCheckbox
                    className="flex flex-col gap-y-3.5"
                    options={selected.relationships}
                    value={relationships}
                    onChange={setRelationships}
                  />
                </StyledCard>
              )}
              {isRoleNeeded && reviewerRoles.length > 1 && (
                <StyledCard className="mt-6">
                  <p className="mb-3">
                    <b>{t('Choose role')}</b>
                  </p>
                  <StyledSelectRadioButton
                    className="flex flex-col gap-y-3"
                    options={reviewerRoles.map((role) => {
                      return { id: role.id.toString(), label: role.description };
                    })}
                    value={role}
                    onChange={setRole}
                  />
                </StyledCard>
              )}
              <div className="mt-6">
                <StyledSelectRadioButton
                  label={t('File format:')}
                  options={
                    [
                      { id: 'json', label: 'JSON' },
                      { id: 'excel', label: 'Excel' },
                    ] as { id: FileFormat; label: string }[]
                  }
                  value={format}
                  onChange={(value) => setFormat(value as FileFormat)}
                  className="flex items-center pl-4 justify-content-start"
                />
              </div>
              <div className="mt-6">
                <Button
                  style={{ width: '24rem', fontSize: '1rem' }}
                  variant="solid"
                  disabled={(isRoleNeeded && !role) || !format}
                  onClick={() => {
                    let url = new URL(getHref(`/web/export/${selected?.id}`));
                    url = addSearchParams(url, { relationships: JSON.stringify(relationships), role: role, format });
                    downloadFile(url.toString());
                  }}
                >
                  {t('Export data')}
                </Button>
              </div>
            </>
          )}
        </div>
      );
      break;
    case 'api':
      content = <AccessTokenManager />;
      break;
  }

  return (
    <>
      <div className="flex flex-col items-center">
        <Tabs className="flex-grow mb-4 px-40 text-sm gap-x-10">
          <TabsItem label={t('Export to file')} selected={section == 'file'} onClick={() => setSection('file')} />
          {/*<TabsItem label={t('API access')} selected={section == 'api'} onClick={() => setSection('api')} />*/}
        </Tabs>
      </div>
      <div className="mt-16 mb-4">{content}</div>
    </>
  );
};

const StyledMessageWrapper = styled.div`
  max-width: 32.5rem;

  h2 {
    font-size: 1.45rem;
  }
  p {
    font-size: 1.1rem;
    line-height: 1.5rem;
  }
  .fileInputWrapper {
    margin-bottom: 0;
  }
  .radiobutton-wrapper {
    label {
      margin-right: 0;
    }
  }
`;

const StyledIcon = styled.div`
  svg {
    width: 6.25rem;
    height: 6.25rem;
  }
`;

const StyledCard = styled(Card)`
  width: 24rem;
`;

const StyledSelectRadioButton = styled(SelectRadioButton)`
  width: 24rem;
  label {
    margin-right: 1rem;
    padding-top: 0.125rem;
    font-size: 1rem;
  }
  input {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const mapStateToProps = (state: AppState) => ({
  roleById: selectTrackRoles(state),
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(DataAPIAccessPage);
